import type { OrderBookingSnapshot } from './checkoutOrderTypes';

export type ShopOrderStatus =
  | 'Pending'
  | 'Accepted'
  | 'Packed'
  | 'Dispatched'
  | 'InTransit'
  | 'Delivered'
  | 'Cancelled'
  | 'Rejected'
  | 'Refunded';

export type AggregateCheckoutStatus =
  | 'Processing'
  | 'PartiallyShipped'
  | 'PartiallyDelivered'
  | 'Completed'
  | 'PartiallyCancelled'
  | 'Cancelled'
  | 'PartiallyRefunded'
  | 'Refunded';

const STATUS_ALIASES: Record<string, ShopOrderStatus> = {
  pending: 'Pending',
  accepted: 'Accepted',
  confirmed: 'Accepted',
  packed: 'Packed',
  packing: 'Packed',
  dispatched: 'Dispatched',
  shipped: 'Dispatched',
  intransit: 'InTransit',
  'in transit': 'InTransit',
  'in-transit': 'InTransit',
  delivered: 'Delivered',
  completed: 'Delivered',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  rejected: 'Rejected',
  refunded: 'Refunded',
  return: 'Refunded',
};

export function normalizeShopStatus(raw: string | null | undefined): ShopOrderStatus {
  if (!raw?.trim()) return 'Pending';
  const key = raw.trim().toLowerCase().replace(/\s+/g, '');
  return STATUS_ALIASES[key] ?? STATUS_ALIASES[raw.trim().toLowerCase()] ?? 'Pending';
}

/** Infer status from milestone timestamps when `status` string is stale or generic. */
export function resolveShopOrderStatus(booking: OrderBookingSnapshot): ShopOrderStatus {
  const fromRefund =
    booking.refund_status?.trim() &&
    normalizeShopStatus(booking.refund_status) === 'Refunded'
      ? 'Refunded'
      : null;

  if (fromRefund) return fromRefund;

  const fromString = normalizeShopStatus(booking.status);
  if (fromString !== 'Pending' || !hasAnyMilestone(booking)) {
    if (fromString === 'Pending' && hasAnyMilestone(booking)) {
      return inferFromMilestones(booking);
    }
    return fromString;
  }

  return inferFromMilestones(booking);
}

function hasAnyMilestone(booking: OrderBookingSnapshot): boolean {
  return Boolean(
    booking.rejected_at ||
      booking.cancelled_at ||
      booking.delivered_at ||
      booking.inTransit_at ||
      booking.dispatched_at ||
      booking.accepted_at
  );
}

function inferFromMilestones(booking: OrderBookingSnapshot): ShopOrderStatus {
  if (booking.rejected_at) return 'Rejected';
  if (booking.cancelled_at) return 'Cancelled';
  if (booking.delivered_at) return 'Delivered';
  if (booking.inTransit_at) return 'InTransit';
  if (booking.dispatched_at) return 'Dispatched';
  if (booking.accepted_at) return 'Accepted';
  return 'Pending';
}

export function shopStatusLabel(status: ShopOrderStatus): string {
  if (status === 'InTransit') return 'In Transit';
  return status;
}

export function aggregateStatusLabel(status: AggregateCheckoutStatus): string {
  const labels: Record<AggregateCheckoutStatus, string> = {
    Processing: 'Processing',
    PartiallyShipped: 'Partially Shipped',
    PartiallyDelivered: 'Partially Delivered',
    Completed: 'Completed',
    PartiallyCancelled: 'Partially Cancelled',
    Cancelled: 'Cancelled',
    PartiallyRefunded: 'Partially Refunded',
    Refunded: 'Refunded',
  };
  return labels[status];
}

/** CSS slug for badge classes (lowercase, no spaces). */
export function statusCssSlug(status: string): string {
  return status
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9-]/g, '');
}

export function aggregateStatusCssSlug(status: AggregateCheckoutStatus): string {
  return statusCssSlug(aggregateStatusLabel(status));
}

export function shopStatusCssSlug(status: ShopOrderStatus): string {
  return statusCssSlug(shopStatusLabel(status));
}

export interface FulfillmentMilestone {
  key: string;
  label: string;
  reached: boolean;
  timestamp: string | null;
}

export function buildFulfillmentTimeline(
  booking: OrderBookingSnapshot
): FulfillmentMilestone[] {
  const current = resolveShopOrderStatus(booking);
  const order: ShopOrderStatus[] = [
    'Pending',
    'Accepted',
    'Dispatched',
    'InTransit',
    'Delivered',
  ];
  const terminal = ['Cancelled', 'Rejected', 'Refunded'] as const;
  if (terminal.includes(current as (typeof terminal)[number])) {
    return [
      {
        key: current.toLowerCase(),
        label: shopStatusLabel(current),
        reached: true,
        timestamp:
          current === 'Cancelled'
            ? booking.cancelled_at ?? null
            : current === 'Rejected'
              ? booking.rejected_at ?? null
              : null,
      },
    ];
  }

  const rank = (s: ShopOrderStatus) => order.indexOf(s);
  const currentRank = rank(current);

  return [
    {
      key: 'pending',
      label: 'Order Placed',
      reached: true,
      timestamp: booking.created_at,
    },
    {
      key: 'accepted',
      label: 'Accepted',
      reached: currentRank >= rank('Accepted'),
      timestamp: booking.accepted_at ?? null,
    },
    {
      key: 'dispatched',
      label: 'Dispatched',
      reached: currentRank >= rank('Dispatched'),
      timestamp: booking.dispatched_at ?? null,
    },
    {
      key: 'intransit',
      label: 'In Transit',
      reached: currentRank >= rank('InTransit'),
      timestamp: booking.inTransit_at ?? null,
    },
    {
      key: 'delivered',
      label: 'Delivered',
      reached: currentRank >= rank('Delivered'),
      timestamp: booking.delivered_at ?? null,
    },
  ];
}
